// "Did that actually help?" — the loop guard for overflow reworks.
//
// The studio can auto-hand a badly overflowing proposal back to the model to
// rework. Each rework is a NEW proposal, which measures itself, which can hand
// back again. Nothing in that chain ever checked whether the number was going
// DOWN, so a run went 248px → 254px → 599px: the model followed the prompt's
// advice into a two-column re-flow that made the slide taller, said so, and
// carried on (Jake, 2026-08-02).
//
// The build engine has always had this rule (OVERFLOW_LOOP_RULE in
// agent/overflow-check.ts): every edit must reduce the reported number, and if
// it doesn't, put the slide back. Chat only ever had it as prose — and prose is
// exactly what the OpenCode models weigh badly. We already MEASURE the number, so
// this is a fact we can check rather than a rule we have to ask for.
//
// Deliberately does NOT auto-revert. Discarding someone's slide because a
// proxy got bigger is the sort of "helpful" destruction that produced the
// 2026-06 auto-tighten revert. It stops the loop and lets the card say what
// happened; Discard change is right there and puts the previous version back.

// slideId → the last overage (px, at the 1920 reference) seen for it.
const lastOverBySlide = new Map<string, number>();

/** `previous` is the overage of the round before (null on the first), and
 *  `worse` is true when this round is genuinely bigger — the signal to stop
 *  reworking. */
export type OverflowRound = { previous: number | null; worse: boolean };

/** Record this round's overage and report how it compares with the last one. */
export function noteOverflow(slideId: string | null | undefined, over: number | null | undefined): OverflowRound {
  if (!slideId || typeof over !== 'number' || !(over > 0)) return { previous: null, worse: false };
  // One lookup, not has()+get(): every stored value is a positive number, so
  // "absent" and "undefined" cannot mean different things here.
  const previous = lastOverBySlide.get(slideId) ?? null;
  lastOverBySlide.set(slideId, over);
  // A few px either way is measurement noise, not a regression — the same
  // rounding the tolerance itself is built to absorb. Only a real increase
  // counts, or a slide that wobbles by 2px would stop its own rework.
  const worse = previous !== null && over > previous + 8;
  return { previous, worse };
}

/** Forget a slide's history — call when its proposal is settled (kept,
 *  discarded, reverted), so the NEXT edit starts a fresh comparison instead of
 *  being judged against a version nobody is looking at any more. */
export function clearOverflowWatch(slideId: string | null | undefined) {
  if (slideId) lastOverBySlide.delete(slideId);
}

/** Test seam. */
export function _resetOverflowWatch() {
  lastOverBySlide.clear();
}
